/**
 * Resources (prototype v108 §7): quick links to the external tools used by each department. Admin sees all approved
 * resources; every other role sees its own (src/lib/staging/resources.js). Each opens in a new tab.
 */
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { resourcesFor, resourcesNote, resourceRoles } from '@/lib/staging/resources';
import { useStaging } from '../StagingLayout';

/** A file the console serves to signed-in staff (the Sales Playbook): fetched with the session, opened in a new tab. */
async function openFile(path: string) {
  const tab = window.open('', '_blank');
  try {
    const { data: { session } } = await supabaseAdmin.auth.getSession();
    const res = await fetch(path, { headers: { Authorization: `Bearer ${session?.access_token}` } });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || `HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url; else window.location.href = url;
  } catch (e: any) {
    tab?.close();
    toast.error(`Could not open: ${e.message || e}`);
  }
}

export default function ResourcesPage() {
  const { role } = useStaging();
  const list = resourcesFor(role);
  return (
    <div className="p-8 space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Resources</h2>
        <p className="text-sm text-muted-foreground mt-1">Quick links to external tools used by your department.</p>
        <p className="text-xs text-muted-foreground mt-1">{resourcesNote(role)}</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {list.map((r) => (
          <div key={r.key} className="rounded-xl border bg-card p-4 shadow-sm flex flex-col gap-2">
            <div>
              <div className="font-medium">{r.name}</div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{resourceRoles(r.key).join(' · ') || 'Admin'}</div>
            </div>
            <p className="text-sm text-muted-foreground flex-1">{r.desc}</p>
            {r.file
              ? <button type="button" onClick={() => openFile(r.file!)} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline self-start">Open Resource <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></button>
              : <a href={r.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline self-start">Open Resource <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a>}
          </div>
        ))}
      </div>
    </div>
  );
}
